import { Drawer, Group, Select, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import { MaterialTypeCategory } from "@tfg/db"
import { Button, SearchInput } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useState, useMemo } from "react"

import { materialTypeSchema } from "@tfg/types/schemas/admin"

import { HeaderControlsProvider } from "~/components/contexts/useHeaderControlsContext"
import {
  FindMaterialTypesFilters,
  FindMaterialTypesQueryResponse,
  FindMaterialTypesRequestSortsDto,
  useFindMaterialTypes,
} from "~/gen/api"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { useFindOperationChainsQuery } from "~/pages/Admin/Ingestion/OpChains/hooks/useFindOperationChainsQuery"
import { MaterialTypesSort } from "~/pages/Admin/MasterLists/MaterialTypes/MaterialTypesSort"
import { useCreateMaterialTypeMutation } from "~/pages/Admin/MasterLists/MaterialTypes/hooks"
import { useGetEcobalyseMaterialTypesQuery } from "~/pages/Admin/MasterLists/MaterialTypes/hooks/useGetEcobalyseMaterialTypesQuery"
import { FormFieldConfig, TypesForm } from "~/pages/Admin/components/TypesForm"

import { MaterialTypesTable } from "./MaterialTypesTable"

const MaterialTypesAdminWrapperContent = () => {
  const [search, setSearch] = useState("")
  const [filters, setFilters] = useState<FindMaterialTypesFilters>({})
  const [sorts, setSorts] = useState<FindMaterialTypesRequestSortsDto>({})
  const { data, totalCount, setPageBasedPagination, pageBasedPagination } =
    usePaginatedQuery<
      typeof useFindMaterialTypes,
      FindMaterialTypesQueryResponse
    >(useFindMaterialTypes, {
      search,
      filters,
      sorts,
    })
  const { data: materialOpChains, isLoading: materialOpChainsLoading } =
    useFindOperationChainsQuery({
      type: "MATERIAL",
      showDeleted: false,
    })
  const {
    data: ecobalyseMaterialTypes,
    isLoading: ecobalyseMaterialTypesLoading,
  } = useGetEcobalyseMaterialTypesQuery()

  const [drawerOpen, drawerActions] = useDisclosure(false)
  const { mutate: createMaterialType } = useCreateMaterialTypeMutation({
    onSuccess: drawerActions.close,
  })

  // Create form configuration reactively based on loaded data
  const formConfig = useMemo(
    () => ({
      validate: zod4Resolver(materialTypeSchema),
      initialValues: {
        key: "",
        category: MaterialTypeCategory.SYNTHETIC,
        ecobalyseId: "",
        operationChainId: materialOpChains?.data?.[0]?.id ?? "",
      },
    }),
    [materialOpChains?.data]
  )

  const fields: FormFieldConfig[] = useMemo(
    () => [
      {
        type: "text",
        name: "key",
        label: "Key",
        placeholder: "Material Type Key",
      },
      {
        type: "select",
        name: "category",
        label: "Category",
        placeholder: "Material Type Category",
        options: Object.values(MaterialTypeCategory).map((value) => ({
          label: value,
          value,
        })),
      },
      {
        type: "text",
        name: "ecobalyseId",
        label: "Ecobalyse Id",
        placeholder: "Ecobalyse Id",
      },
      {
        type: "select",
        name: "operationChainId",
        label: "Operation Chain",
        placeholder: "Material Operation Chain",
        options:
          materialOpChains?.data?.map((opChain) => ({
            label: opChain.name,
            value: opChain.id,
          })) ?? [],
      },
    ],
    [materialOpChains?.data]
  )

  // Check if required data is loaded
  const isDataLoaded =
    !materialOpChainsLoading &&
    !ecobalyseMaterialTypesLoading &&
    materialOpChains?.data

  return (
    <Stack>
      <Group justify="space-between">
        <Group>
          <SearchInput
            inputValue={search}
            placeholder="Search Material Types"
            onChange={(value) => setSearch(value.toString())}
          />
          <MaterialTypesSort sorts={sorts} setSorts={setSorts} />
          <Select
            data={
              materialOpChains?.data.map((opChain) => ({
                label: opChain.name,
                value: opChain.name,
              })) ?? []
            }
            value={filters.operationChainName}
            placeholder="Filter by Operation Chain"
            onChange={(value) => {
              if (value) {
                setFilters((prev) => ({ ...prev, operationChainName: value }))
              } else {
                setFilters((prev) => {
                  const { operationChainName, ...rest } = prev
                  return rest
                })
              }
            }}
            clearable
          />
        </Group>
        <Button onClick={drawerActions.open} icon={<IconPlus />}>
          New Material Type
        </Button>
      </Group>
      <Drawer
        position="right"
        opened={drawerOpen}
        onClose={() => drawerActions.close()}
        title="Create Material Type"
        size="lg"
      >
        {isDataLoaded ? (
          <TypesForm
            elements={fields}
            submitLabel="Create"
            formConfig={formConfig}
            mutate={createMaterialType}
          />
        ) : (
          <div>Loading form data...</div>
        )}
      </Drawer>
      {data?.data && (
        <MaterialTypesTable
          ecobalyseMaterialTypes={ecobalyseMaterialTypes ?? []}
          materialTypes={data.data}
          total={totalCount}
          materialOpChains={materialOpChains?.data ?? []}
          pagination={pageBasedPagination}
          setPagination={setPageBasedPagination}
        />
      )}
    </Stack>
  )
}

export const MaterialTypesAdminWrapper = () => {
  return (
    <HeaderControlsProvider>
      <MaterialTypesAdminWrapperContent />
    </HeaderControlsProvider>
  )
}
